// A lot of this file was copied from getFileTypeIconProps in file-type-icons.
// This really belongs in Fabric so that it can be updated if the urls change.
// This is being added here due to time constraints.
// See the follow url for more information on the cdn urls:
// https://developer.microsoft.com/en-us/fabric#/styles/brand-icons

import * as React from 'react';
import { IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { FileTypeIconMap } from '@uifabric/file-type-icons/lib/FileTypeIconMap';
import { registerIcons, IIconOptions } from '@uifabric/styling';
import { brandIconMap } from './brandIconMap';

let _extensionToFileType: { [key: string]: string };
let _fileTypeToBrand: { [key: string]: string };

const DEFAULT_PNG_SIZE: FileTypeIconSize = 16;
const DEFAULT_SVG_SIZE = 48;
const BASE_URL = 'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/';
const ICON_SIZES: FileTypeIconSize[] = [16, 20, 32, 40, 48, 64, 96];
const FILE_TYPES: ImageFileType[] = ['svg', 'png'];

export type FileTypeIconSize = 16 | 20 | 32 | 40 | 48 | 64 | 96;
export type ImageFileType = 'svg' | 'png';

export interface IBrandTypeIconOptions {
  /**
   * The file extension, such as .pptx, for which you need an icon.
   */
  extension: string;
  /**
   * The size of the icon in pixels. Defaults to 16.
   */
  size?: FileTypeIconSize;
  /**
   * The type of image file to use. Can be svg or png.
   * Defaults to svg.
   */
  imageFileType?: ImageFileType;
}

/**
 * This function returns properties for a file type icon given the IBrandTypeIconOptions.
 * It accounts for different device pixel ratios. For example,
 * getBrandTypeIconProps({extension: 'doc', size: 16, imageFileType: 'png'})
 * will return { iconName: 'docx16_2x_png' } if the devicePixelRatio is 2.
 *
 * @param options
 */
export function getBrandTypeIconProps(options: IBrandTypeIconOptions): IIconProps | undefined {
  // First, obtain the file type of the icon using the extension.
  const fileType = _getFileTypeFromExtension(options.extension);

  // Then get the brand that the file type belongs to
  const brand = _getBrandFromFileType(fileType);

  if (!brand) {
    return undefined;
  }

  // Next, obtain the suffix using the icon size, user's device pixel ratio, and
  // preference for svg or png
  const imageFileType = options.imageFileType || 'svg';
  const size: FileTypeIconSize = _getSize(imageFileType, options.size);
  const scale: string = _getScale(size, imageFileType);

  return {
    iconName: `${imageFileType}/${brand}_${size}x${scale}.${imageFileType}`
  };
}

function _getFileTypeFromExtension(extension: string): string {
  if (!_extensionToFileType) {
    _extensionToFileType = {};

    for (const iconName in FileTypeIconMap) {
      if (FileTypeIconMap.hasOwnProperty(iconName)) {
        const extensions = FileTypeIconMap[iconName].extensions;

        if (extensions) {
          for (let i = 0; i < extensions.length; i++) {
            _extensionToFileType[extensions[i]] = iconName;
          }
        }
      }
    }
  }

  // Strip periods, force lowercase.
  extension = extension.replace('.', '').toLowerCase();

  return _extensionToFileType[extension];
}

function _getBrandFromFileType(fileType: string): string {
  if (!_fileTypeToBrand) {
    _fileTypeToBrand = {};

    for (const brand in brandIconMap) {
      if (brandIconMap.hasOwnProperty(brand)) {
        const fileTypes = brandIconMap[brand];

        for (const type of fileTypes) {
          _fileTypeToBrand[type] = brand;
        }
      }
    }
  }

  return _fileTypeToBrand[fileType];
}

function _getSize(imageFileType: ImageFileType, size?: FileTypeIconSize): FileTypeIconSize {
  if (imageFileType === 'svg') {
    // Only 16 and 48 are available for svg
    return (size === 16 || size === 48) ? size : DEFAULT_SVG_SIZE;
  }

  return size || DEFAULT_PNG_SIZE;
}

function _getScale(size: FileTypeIconSize, imageFileType: ImageFileType): string {
  let devicePixelRatio: number = window.devicePixelRatio;
  let devicePixelRatioSuffix = '1'; // Default is 1x

  // SVGs scale well, so you can generally use the default image.
  // 1.5x is a special case where SVGs need a different image.
  if (imageFileType === 'svg' && 1 < devicePixelRatio && devicePixelRatio <= 1.5) {
    // Currently missing 1.5x SVGs at size 20, snap to 1x for now
    if (size !== 20) {
      devicePixelRatioSuffix = '1_5';
    }
  } else if (imageFileType === 'png') {
    // To look good, PNGs should use a different image for higher device pixel ratios
    if (1 < devicePixelRatio && devicePixelRatio <= 1.5) {
      // Currently missing 1.5x icons for size 20, snap to 2x for now
      devicePixelRatioSuffix = size === 20 ? '2' : '1_5';
    } else if (1.5 < devicePixelRatio && devicePixelRatio <= 2) {
      devicePixelRatioSuffix = '2';
    } else if (2 < devicePixelRatio && devicePixelRatio <= 3) {
      devicePixelRatioSuffix = '3';
    } else if (3 < devicePixelRatio) {
      devicePixelRatioSuffix = '4';
    }
  }

  return devicePixelRatioSuffix;
}

export function initializeBrandTypeIcons(baseUrl: string = BASE_URL, options?: Partial<IIconOptions>): void {
  ICON_SIZES.forEach((size: FileTypeIconSize) => {
    for (const brand in brandIconMap) {
      if (brandIconMap.hasOwnProperty(brand)) {
        FILE_TYPES.forEach((imageFileType: ImageFileType) => {
          const scale = _getScale(size, imageFileType);
          registerIcons(
            {
              fontFace: {},
              style: {
                width: scale === '1' ? size : '100%',
                height: scale === '1' ? size : '100%',
                overflow: 'hidden'
              },
              icons: {
                // tslint:disable-next-line: max-line-length
                [`${imageFileType}/${brand}_${size}x${scale}.${imageFileType}`]: <img src={ `${baseUrl}${imageFileType}/${brand}_${size}x${scale}.${imageFileType}` } />
              }
            },
            options
          );
        });
      }
    }
  });
}